/**
 * The draft a profile form holds, and the two pure conversions between it and
 * the wire — the same split `teamDraft` uses, and for a sharper reason here.
 *
 * `PUT /me/profile` REPLACES the stored profile: the server rebuilds the item
 * from the body alone, so a field left out of the request is not "unchanged",
 * it is erased. The edit form and the signup wizard both post to that one
 * endpoint, so the body has to be built in one place that always emits every
 * writable field. A form that sent `{ displayName }` on a name change would
 * drop the phone number of every coach who had set one.
 *
 * Every field is a string, because that is what a form control holds.
 */
import type { Profile } from "./types";

export interface ProfileDraft {
  displayName: string;
  phone: string;
}

export const emptyDraft = (): ProfileDraft => ({ displayName: "", phone: "" });

/**
 * Entity -> draft. Both fields are defaulted: `phone` is optional on the wire,
 * and an input bound to `undefined` silently becomes an uncontrolled one — the
 * coach would type into a field React then refuses to update.
 */
export function toDraft(profile: Profile): ProfileDraft {
  return {
    displayName: profile.displayName ?? "",
    phone: profile.phone ?? "",
  };
}

/**
 * Draft -> request body, carrying every writable field every time (see above).
 * A blank phone is sent as `undefined` rather than "": `JSON.stringify` omits
 * the key entirely, and `updateProfileSchema` reads an absent optional as "not
 * supplied" while an empty string would pass validation and store a blank.
 *
 * Clearing a phone on purpose therefore works — the replace drops it either
 * way — and so does never having had one.
 */
export function toBody(draft: ProfileDraft) {
  return {
    displayName: draft.displayName.trim(),
    phone: draft.phone.trim() || undefined,
  };
}

/** Has the coach changed anything? Compared trimmed, so stray spaces are not edits. */
export function isDirty(draft: ProfileDraft, saved: ProfileDraft): boolean {
  return (
    draft.displayName.trim() !== saved.displayName.trim() ||
    draft.phone.trim() !== saved.phone.trim()
  );
}
